import { useState } from "react";
import {
  MessageSquarePlusIcon,
  PencilLineIcon,
  RotateCcwIcon,
} from "lucide-react";

import { SadieSimulatorEditedChip } from "./SadieSimulatorEditedChip";
import { SadieSimulatorResetButton } from "./SadieSimulatorResetButton";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@ui/sheet";
import { Textarea } from "@ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui/tooltip";

// A dedicated editor for just the opener-writing instructions, separate from
// the whole-prompt editor. Applying re-renders the whole prompt server-side
// (same as a persona or matched-opportunity change), so edits here always
// produce a prompt the agent could actually run, never a hand-spliced one.
export const SadieSimulatorOpenerField = ({
  defaultValue,
  disabled,
  isDirty,
  onChange,
  onReset,
  value,
}: {
  defaultValue: string;
  disabled: boolean;
  isDirty: boolean;
  onChange: (value: string) => void;
  onReset: () => void;
  value: string;
}) => {
  /* React Hooks */
  const [draft, setDraft] = useState(value);

  /* Handlers */
  const handleOpenChange = (open: boolean) => {
    if (open) setDraft(value);
  };

  const handleApply = () => {
    if (draft.trim().length > 0) onChange(draft);
  };

  const handleResetDraft = () => {
    setDraft(defaultValue);
    onReset();
  };

  /* Render */
  return (
    <Column className="gap-1.5">
      <Row className="h-7 items-center justify-between">
        <Row className="items-center gap-2">
          <MessageSquarePlusIcon className="h-4 w-4 shrink-0 text-slate-400" />
          <Text className="text-sm font-medium text-slate-700">Opener</Text>
          {isDirty ? <SadieSimulatorEditedChip /> : null}
        </Row>
        <Row className="items-center gap-0.5">
          <SadieSimulatorResetButton
            disabled={disabled || !isDirty}
            onClick={onReset}
          />
          <Sheet onOpenChange={handleOpenChange}>
            <Tooltip>
              <TooltipTrigger asChild>
                <SheetTrigger asChild>
                  <Button
                    aria-label="Edit opener instructions"
                    className="h-7 w-7 text-slate-500"
                    disabled={disabled}
                    size="icon"
                    variant="ghost"
                  >
                    <PencilLineIcon className="h-3.5 w-3.5" />
                  </Button>
                </SheetTrigger>
              </TooltipTrigger>
              <TooltipContent>Edit opener instructions</TooltipContent>
            </Tooltip>
            <SheetContent className="flex w-full flex-col gap-4 sm:w-[600px] sm:max-w-[600px]">
              <SheetHeader>
                <Row className="items-center gap-2">
                  <SheetTitle>Opener instructions</SheetTitle>
                  {isDirty ? <SadieSimulatorEditedChip /> : null}
                </Row>
                <SheetDescription>
                  How Sadie should write the opening message for this matched
                  opportunity. Applies on the next turn.
                </SheetDescription>
              </SheetHeader>
              <Textarea
                className="min-h-0 flex-1 resize-none rounded-lg p-3 font-mono text-xs leading-relaxed text-slate-800 md:text-xs"
                onChange={(e) => {
                  setDraft(e.target.value);
                }}
                spellCheck={false}
                value={draft}
              />
              <SheetFooter className="items-center sm:justify-between">
                <Text className="text-xs text-slate-500">
                  {draft.length.toLocaleString()} characters
                </Text>
                <Row className="items-center gap-2">
                  <Button
                    disabled={!isDirty}
                    onClick={handleResetDraft}
                    size="sm"
                    variant="ghost"
                  >
                    <RotateCcwIcon className="h-3.5 w-3.5" />
                    Reset to default
                  </Button>
                  <SheetClose asChild>
                    <Button
                      disabled={draft.trim().length === 0}
                      onClick={handleApply}
                      size="sm"
                    >
                      Done
                    </Button>
                  </SheetClose>
                </Row>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </Row>
      </Row>
      <Text className="text-xs text-slate-500">
        {value.length.toLocaleString()} characters. How Sadie writes the opening
        message.
      </Text>
    </Column>
  );
};
